// Local-mode mocks for the RADIUS console: RADIUS Servers (/radius/ListServers, CreateServer,
// RenameServer, DeleteServer) and Network Devices (PAM's /network_device/ListDevices,
// CreateDevice, DeleteDevice, plus /radius/DeviceActivity and /radius/UpdateDevice).
// Shapes as authnull-service internal/radius and internal/pam/handler/networkdevice answer.
//
// Stateful until reload. Three servers: radius-lab (healthy, five passing checks, two devices),
// radius-dr (degraded: config_valid fails), radius-old (never connected). Three devices: one
// discovered, one active, one inactive.
//
// The mock adapter always answers 200, so the API's 409s (a taken name, deleting a server that
// devices still use) can't be reproduced as errors: those calls answer with the API's message
// and change nothing. The pages check both cases before calling anyway.

const MIN = 60 * 1000;
const DAY = 24 * 60 * MIN;
const iso = (msAgo) => new Date(Date.now() - msAgo).toISOString();
const wrap = (data) => ({ code: 200, status: "success", data });

const CHECKS_OK = [
  { name: "freeradius_running", ok: true, detail: "freeradius.service active" },
  { name: "udp_1812_listening", ok: true, detail: "0.0.0.0:1812" },
  { name: "config_valid", ok: true, detail: "radiusd -C: configuration appears to be OK" },
  { name: "ad_reachable", ok: true, detail: "dc1.authnull.lab:389 in 4 ms" },
  { name: "authn_reachable", ok: true, detail: "http://10.10.0.9:2882 in 11 ms" },
];

export const SERVER_LAB = "7d1c2b9e-0f3a-4c55-9a7e-2b1f6d0c8e41";
export const SERVER_DR = "b3e8f0a2-6c1d-4f7e-8a95-0d2c4e6f8a13";
const SERVER_OLD = "e5a7c9b1-3d2f-4b6a-9c8e-1f0a2b3c4d57";

// lastSeenAt is refreshed on every list read, so the lab and DR servers stay Active.
const servers = [
  {
    id: SERVER_LAB,
    name: "radius-lab",
    type: "freeradius",
    sourceIp: "10.10.0.20",
    status: "healthy",
    lastSeenOffsetMs: 32 * 1000,
    lastHealth: { hostname: "radius-lab.authnull.lab", checks: CHECKS_OK },
    bridgeVersion: "1.4.2",
    lastLoginMs: Date.now() - 3 * MIN,
    loginCount: 412,
    createdAt: iso(40 * DAY),
  },
  {
    id: SERVER_DR,
    name: "radius-dr",
    type: "freeradius",
    sourceIp: "10.20.0.20",
    status: "degraded",
    lastSeenOffsetMs: 48 * 1000,
    lastHealth: {
      hostname: "radius-dr.authnull.lab",
      checks: CHECKS_OK.map((c) =>
        c.name === "config_valid"
          ? { name: "config_valid", ok: false, detail: "radiusd -C: /etc/freeradius/3.0/clients.conf[14]: Parse error" }
          : c,
      ),
    },
    bridgeVersion: "1.4.1",
    lastLoginMs: Date.now() - 2 * DAY,
    loginCount: 37,
    createdAt: iso(20 * DAY),
  },
  {
    id: SERVER_OLD,
    name: "radius-old",
    type: "freeradius",
    sourceIp: "10.30.0.5",
    status: "never_connected",
    lastSeenOffsetMs: null,
    lastHealth: null,
    bridgeVersion: null,
    lastLoginMs: 0,
    loginCount: 0,
    createdAt: iso(2 * DAY),
  },
];

// PAM's network_devices rows (ListDevices), keyed by uuid like the real table.
const DEVICE_DISCOVERED = "3f6b2a10-8c4d-4e1f-9a2b-5c6d7e8f9a01";
const DEVICE_ACTIVE = "4a7c3b21-9d5e-4f2a-8b3c-6d7e8f9a0b12";
const DEVICE_INACTIVE = "5b8d4c32-ae6f-4a3b-9c4d-7e8f9a0b1c23";

const devices = [
  {
    // Registered itself on its first login: named after its address until someone names it.
    id: DEVICE_DISCOVERED,
    device_type: "",
    name: "203.0.113.40",
    device_id: "",
    ip_address: "203.0.113.40",
    client_ip: "203.0.113.40",
    nas_identifier: "",
    nas_port: "0",
    nas_port_type: "",
    nas_port_id: "",
  },
  {
    id: DEVICE_ACTIVE,
    device_type: "VPN",
    name: "Office VPN",
    device_id: "",
    ip_address: "10.10.0.30",
    client_ip: "10.10.0.30",
    nas_identifier: "vpn-edge-01",
    nas_port: "0",
    nas_port_type: "",
    nas_port_id: "",
  },
  {
    id: DEVICE_INACTIVE,
    device_type: "Wi-Fi",
    name: "Branch Wi-Fi",
    device_id: "",
    ip_address: "10.40.1.2",
    client_ip: "10.40.1.2",
    nas_identifier: "wlc-branch",
    nas_port: "0",
    nas_port_type: "",
    nas_port_id: "",
  },
];

// DeviceActivity: the server each device sends logins to, its last login, discovered.
const activity = {
  [DEVICE_DISCOVERED]: { radiusServerId: SERVER_LAB, lastLoginMs: Date.now() - 3 * MIN, discovered: true },
  [DEVICE_ACTIVE]: { radiusServerId: SERVER_LAB, lastLoginMs: Date.now() - 2 * 60 * MIN, discovered: false },
  [DEVICE_INACTIVE]: { radiusServerId: null, lastLoginMs: Date.now() - 45 * DAY, discovered: false },
};

const deviceStatus = (a) => {
  if (a?.discovered) return "discovered";
  return a?.lastLoginMs && Date.now() - a.lastLoginMs <= 30 * DAY ? "active" : "inactive";
};

const devicesUsing = (id) => Object.values(activity).filter((a) => a.radiusServerId === id).length;

const listedServer = ({ lastSeenOffsetMs, ...s }) => ({
  ...s,
  lastSeenAt: lastSeenOffsetMs == null ? null : iso(lastSeenOffsetMs),
  devicesUsing: devicesUsing(s.id),
});

const nameTaken = (name, exceptId) =>
  servers.some((s) => s.id !== exceptId && s.name.toLowerCase() === String(name || "").trim().toLowerCase());

const newUuid = () =>
  "xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx".replace(/x/g, () => Math.floor(Math.random() * 16).toString(16));

// Lower-case URL substrings; matched before mockData's static "/radius" entry.
export const RADIUS_MOCKS = [
  {
    pattern: "/radius/listservers",
    respond: () => wrap(servers.map(listedServer)),
  },
  {
    pattern: "/radius/createserver",
    respond: (body = {}) => {
      const name = String(body?.name || "").trim();
      if (nameTaken(name)) return { code: 409, status: "failed", message: "a server with that name already exists" };
      const row = {
        id: newUuid(),
        name,
        type: body?.type || "freeradius",
        sourceIp: String(body?.sourceIp || "").trim(),
        status: "never_connected",
        lastSeenOffsetMs: null,
        lastHealth: null,
        bridgeVersion: null,
        lastLoginMs: 0,
        loginCount: 0,
        createdAt: new Date().toISOString(),
      };
      servers.push(row);
      servers.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      return wrap(listedServer(row));
    },
  },
  {
    pattern: "/radius/renameserver",
    respond: (body = {}) => {
      const s = servers.find((x) => x.id === body?.id);
      if (!s) return { code: 404, status: "failed", message: "server not found" };
      if (nameTaken(body?.name, s.id)) return { code: 409, status: "failed", message: "a server with that name already exists" };
      s.name = String(body?.name || "").trim();
      return { code: 200, status: "success", message: "server renamed" };
    },
  },
  {
    pattern: "/radius/deleteserver",
    respond: (body = {}) => {
      const i = servers.findIndex((x) => x.id === body?.id);
      if (i < 0) return { code: 404, status: "failed", message: "server not found" };
      // The real API answers 409 here; the mock adapter can only answer 200.
      if (devicesUsing(body.id) > 0) return { code: 409, status: "failed", message: "network devices still send logins to this server" };
      servers.splice(i, 1);
      return { code: 200, status: "success", message: "server deleted" };
    },
  },
  {
    pattern: "/radius/deviceactivity",
    respond: () =>
      wrap(
        devices.map((d) => {
          const a = activity[d.id] || { radiusServerId: null, lastLoginMs: null, discovered: false };
          return { id: d.id, radiusServerId: a.radiusServerId, lastLoginMs: a.lastLoginMs, discovered: a.discovered, status: deviceStatus(a) };
        }),
      ),
  },
  {
    pattern: "/radius/updatedevice",
    respond: (body = {}) => {
      const d = devices.find((x) => x.id === body?.id);
      if (!d) return { code: 404, status: "failed", message: "device not found" };
      d.name = String(body?.name || "").trim();
      d.device_type = String(body?.deviceType || "").trim();
      d.nas_identifier = String(body?.nasIdentifier || "").trim();
      const a = activity[d.id] || (activity[d.id] = { radiusServerId: null, lastLoginMs: null, discovered: false });
      // Saving names the device: no longer Discovered.
      a.discovered = false;
      // Absent leaves the server as it is; "" clears it.
      if (body && "radiusServerId" in body) a.radiusServerId = body.radiusServerId || null;
      return { code: 200, status: "success", message: "device updated" };
    },
  },
  // PAM's network device API (not wrapped in { code, status, data }).
  {
    pattern: "/network_device/listdevices",
    respond: () => ({
      network_devices: devices,
      total_count: devices.length,
      page_count: 1,
      current_page: 1,
      code: "200",
      message: "success",
    }),
  },
  {
    pattern: "/network_device/createdevice",
    respond: (body = {}) => {
      const id = newUuid();
      devices.push({
        id,
        device_type: body?.device_type || "",
        name: body?.name || "",
        device_id: "",
        ip_address: body?.ip_address || "",
        client_ip: body?.client_ip || "",
        nas_identifier: body?.nas_identifier || "",
        nas_port: "0",
        nas_port_type: "",
        nas_port_id: "",
      });
      activity[id] = { radiusServerId: null, lastLoginMs: null, discovered: false };
      return { id, message: "device created", code: "201" };
    },
  },
  {
    pattern: "/network_device/deletedevice",
    respond: (body = {}) => {
      const i = devices.findIndex((d) => d.client_ip === body?.client_ip);
      if (i >= 0) {
        delete activity[devices[i].id];
        devices.splice(i, 1);
      }
      return { code: "200", message: "device deleted" };
    },
  },
];
